import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { getCourses, getModules, createTest, createQuestion } from '../api';
import logger from '../logger';
import './SetQuiz.css';

const loggedInUserId = 1;

const SetQuiz = () => {
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [questions, setQuestions] = useState([
    { id: uuidv4(), title: 'Untitled question', type: 'Multiple choice', options: ['Option 1', 'Option 2'], correctAnswer: 0 }
  ]);
  const [loading, setLoading] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [errors, setErrors] = useState({});
  const staffId = localStorage.getItem("staffId");

  // 🟢 Fetch Courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const res = await getCourses();

        let rawCourses = [];
        if (Array.isArray(res)) rawCourses = res;
        else if (Array.isArray(res.data)) rawCourses = res.data;
        else if (res.data && Array.isArray(res.data.courses)) rawCourses = res.data.courses;
        else if (res.courses && Array.isArray(res.courses)) rawCourses = res.courses;

        // Normalize course structure
        const courseArray = rawCourses.map(c => ({
          id: c.id || c.course_id || c._id,
          name: c.name || c.title || c.course_name,
        }));

        setCourses(courseArray);
        if (courseArray.length > 0) setSelectedCourse(courseArray[0].id);
      } catch (err) {
        logger.error('Failed to fetch courses', err);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    if (!selectedCourse) {
      setModules([]);
      setSelectedModule('');
      return;
    }

    const fetchModules = async () => {
      try {
        setLoadingModules(true);
        const courseId = Number(selectedCourse);
        const res = await getModules(courseId);
        logger.debug("Modules response:", res);

        let rawModules = [];

        // Case 1: API returns an array
        if (Array.isArray(res)) {
          rawModules = res;
        }
        else if (Array.isArray(res.data)) {
          rawModules = res.data;
        }
        // Case 2: API returns a single module object (your case)
        else if (res.data && typeof res.data === "object" && res.data !== null) {
          rawModules = [res.data];
        }
        else if (res.modules && Array.isArray(res.modules)) {
          rawModules = res.modules;
        }
        else if (res.data && Array.isArray(res.data.modules)) {
          rawModules = res.data.modules;
        }

        // Normalize the modules
        const normalizedModules = rawModules.map((m) => ({
          id: m.id || m.module_id || m._id,
          name: m.name || m.title || m.module_name || "Untitled Module",
        }));

        setModules(normalizedModules);
        setSelectedModule(normalizedModules.length > 0 ? normalizedModules[0].id : '');
      } catch (err) {
        logger.error('Failed to load modules', err);
        setModules([]);
      } finally {
        setLoadingModules(false);
      }
    };

    fetchModules();
  }, [selectedCourse]);

  // 🟢 Validation
  const validateQuestions = () => {
    const newErrors = {};
    questions.forEach((q) => {
      if (!q.title.trim()) newErrors[q.id] = 'Question title cannot be empty';
      if (q.options.length < 2)
        newErrors[q.id] = newErrors[q.id]
          ? `${newErrors[q.id]}. At least 2 options required.`
          : 'At least 2 options required';
      q.options.forEach((opt, i) => {
        if (!opt.trim())
          newErrors[q.id] = newErrors[q.id]
            ? `${newErrors[q.id]}. Option ${i + 1} cannot be empty.`
            : `Option ${i + 1} cannot be empty`;
      });
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🟢 Question management
  const addQuestion = () =>
    setQuestions([
      ...questions,
      { id: uuidv4(), title: 'Untitled question', type: 'Multiple choice', options: ['Option 1', 'Option 2'], correctAnswer: 0 },
    ]);

  const duplicateQuestion = (id) => {
    const q = questions.find((q) => q.id === id);
    setQuestions([...questions, { ...q, id: uuidv4(), title: `${q.title} (Copy)` }]);
  };

  const deleteQuestion = (id) => {
    setQuestions(questions.filter((q) => q.id !== id));
    setErrors({});
  };

  const addOption = (id) => {
    setQuestions(
      questions.map((q) =>
        q.id === id ? { ...q, options: [...q.options, `Option ${q.options.length + 1}`] } : q
      )
    );
    setErrors({});
  };

  const deleteOption = (id, index) => {
    setQuestions(
      questions.map((q) =>
        q.id === id
          ? {
            ...q,
            options: q.options.filter((_, i) => i !== index),
            correctAnswer: q.correctAnswer === index ? 0 : q.correctAnswer,
          }
          : q
      )
    );
    setErrors({});
  };

  const updateQuestionTitle = (id, title) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, title } : q)));
    setErrors({});
  };

  const updateOptionText = (questionId, optionIndex, value) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.map((o, i) => (i === optionIndex ? value : o)) }
          : q
      )
    );
    setErrors({});
  };

  const setCorrectAnswer = (questionId, optionIndex) => {
    setQuestions(questions.map((q) => (q.id === questionId ? { ...q, correctAnswer: optionIndex } : q)));
  };

  // 🟢 Save quiz
  const handleSave = async () => {
    if (!selectedCourse) {
      alert('Please select a course and module');
      return;
    }

    if (!validateQuestions()) {
      alert('Please fix the errors in the questions');
      return;
    }

    setLoading(true);
    try {
      const staffId = localStorage.getItem("staffId");
      if (!staffId) throw new Error("Staff ID missing — please login again.");

      // Create the test
      const testRes = await createTest({
        course_id: Number(selectedCourse),
        module_id: Number(selectedModule),
        score: 0,
        percentile: 0,
        staff_id: Number(staffId),
      });

      // Create questions
      for (const q of questions) {
        const questionOptionsArray = q.options.map((opt, idx) => ({
          text: opt,
          body: opt,
          is_correct: idx === q.correctAnswer,
        }));

        await createQuestion({
          test_id: testRes.id,
          course_id: Number(selectedCourse),
          module_id: Number(selectedModule),
          staff_id: Number(staffId),
          title: q.title,
          body: q.title,
          type: q.type,
          options: questionOptionsArray,
          correct_answer: q.options[q.correctAnswer],
        });
      }

      alert("Quiz created successfully!");
    } catch (err) {
      logger.error("Failed to create quiz:", err.response?.data || err.message);
      alert("Failed to create quiz. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  // 🟢 Render
  return (
    <div className="set-quiz-page">
      <div className="quiz-header">
        <Link to="/quiz" className="back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          Set Quiz
        </Link>
      </div>

      <div className="quiz-content">
        {/* Quiz Settings */}
        <div className="quiz-settings">
          <div className="settings-section">
            <h2>Quiz Settings</h2>
            <div className="form-group">
              <label htmlFor="course-select">Course</label>
              <select
                id="course-select"
                className="form-select"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                disabled={loading}
              >
                <option value="">Select a course</option>
                {courses.filter(course => course.name).map(course => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="module-select">Module</label>
              <select
                id="module-select"
                className="form-select"
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                disabled={loadingModules || !selectedCourse}
              >
                <option value="">Select a module</option>
                {loadingModules ? (
                  <option disabled>Loading modules...</option>
                ) : modules.length > 0 ? (
                  modules.map((mod) => (
                    <option key={mod.id} value={mod.id}>{mod.name}</option>
                  ))
                ) : (
                  <option disabled>No modules found</option>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="questions-section">
          <h2>Questions</h2>
          {questions.map((q) => (
            <div key={q.id} className="question-card">
              {errors[q.id] && <div className="error-message">{errors[q.id]}</div>}
              <div className="question-content">
                <input
                  type="text"
                  className="question-input"
                  value={q.title}
                  onChange={(e) => updateQuestionTitle(q.id, e.target.value)}
                  placeholder="Enter question title"
                />
              </div>
              <div className="options-section">
                <h4>Options</h4>
                {q.options.map((opt, i) => (
                  <div key={i} className={`option-item ${q.correctAnswer === i ? 'correct-answer' : ''}`}>
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      className="option-radio"
                      checked={q.correctAnswer === i}
                      onChange={() => setCorrectAnswer(q.id, i)}
                    />
                    <input
                      type="text"
                      className="option-input"
                      value={opt}
                      onChange={(e) => updateOptionText(q.id, i, e.target.value)}
                      placeholder={`Option ${i + 1}`}
                    />
                    <button className="delete-option" onClick={() => deleteOption(q.id, i)} disabled={q.options.length <= 2}>✖</button>
                  </div>
                ))}
                <button className="add-option-btn" onClick={() => addOption(q.id)}>Add Option</button>
              </div>
              <div className="question-actions">
                <button className="action-btn add-btn" onClick={addQuestion} title="Add new question">➕</button>
                <button className="action-btn copy-btn" onClick={() => duplicateQuestion(q.id)} title="Duplicate question">📄</button>
                <button className="action-btn delete-btn" onClick={() => deleteQuestion(q.id)} disabled={questions.length <= 1} title="Delete question">🗑</button>
              </div>
            </div>
          ))}
        </div>

        <button className="save-button" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Quiz'}
        </button>
      </div>
    </div>
  );
};

export default SetQuiz;
